/**
 * Pure editing model shared by the "Ad Benchmarks" and "SOV/Takeover Benchmarks" tabs.
 *
 * Both sets render as the same editable table. The SOV set adds placeholder
 * rows: format rows with no numbers of their own, which show (and track) the
 * standard tab's values. Editing any field of a placeholder row makes it the
 * row's own; "Reset to placeholder" hands it back to the mirror.
 */

import type {
  AdBenchmarksState,
  AdUnitBenchmarkKey,
  AdUnitBenchmarkValues,
  AdUnitFormatKey,
  BenchmarkSetId,
  ComputedBenchmarkBlock,
  SovBenchmarksState,
} from '../../../services/adBenchmarksApi';

export type BenchmarkField = 'ctr' | 'ctrRange' | 'vcr' | 'vcrRange' | 'note';

export interface BenchmarkTabModel {
  set: BenchmarkSetId;
  order: AdUnitBenchmarkKey[];
  /** Saved values, as resolved by the server (placeholders show their mirror). */
  rows: Partial<Record<AdUnitBenchmarkKey, AdUnitBenchmarkValues>>;
  placeholders: AdUnitBenchmarkKey[];
  /** What the per-row reset restores: defaults (standard, blended row) or the mirror (SOV formats). */
  resetTargets: Partial<Record<AdUnitBenchmarkKey, AdUnitBenchmarkValues>>;
  /** The last GAM computation for this set (null before the first refresh). */
  computed: ComputedBenchmarkBlock | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface BenchmarkDraft {
  rows: Partial<Record<AdUnitBenchmarkKey, AdUnitBenchmarkValues>>;
  placeholders: AdUnitBenchmarkKey[];
}

export function modelFromStandard(state: AdBenchmarksState): BenchmarkTabModel {
  return {
    set: 'standard',
    order: state.order,
    rows: state.values,
    placeholders: [],
    resetTargets: state.defaults,
    computed: state.computed ?? null,
    updatedAt: state.updatedAt,
    updatedBy: state.updatedBy,
  };
}

export function modelFromSov(state: SovBenchmarksState): BenchmarkTabModel {
  const resetTargets: Partial<Record<AdUnitBenchmarkKey, AdUnitBenchmarkValues>> = {};
  for (const key of state.order) {
    resetTargets[key] = key === 'sov-takeover' ? state.defaults[key] : state.mirror[key];
  }
  return {
    set: 'sov',
    order: state.order,
    rows: state.values,
    placeholders: state.placeholders,
    resetTargets,
    computed: state.computed ?? null,
    updatedAt: state.updatedAt,
    updatedBy: state.updatedBy,
  };
}

export const draftFromModel = (model: BenchmarkTabModel): BenchmarkDraft => ({
  rows: { ...model.rows },
  placeholders: [...model.placeholders],
});

/** Only SOV format rows can be placeholders — the blended row always has values. */
export const canBePlaceholder = (model: BenchmarkTabModel, key: AdUnitBenchmarkKey): boolean =>
  model.set === 'sov' && key !== 'sov-takeover';

export function editField(
  draft: BenchmarkDraft,
  key: AdUnitBenchmarkKey,
  field: BenchmarkField,
  value: string,
): BenchmarkDraft {
  const row = draft.rows[key];
  if (!row) return draft;
  return {
    rows: { ...draft.rows, [key]: { ...row, [field]: value } },
    placeholders: draft.placeholders.filter((k) => k !== key),
  };
}

export function resetRow(draft: BenchmarkDraft, model: BenchmarkTabModel, key: AdUnitBenchmarkKey): BenchmarkDraft {
  const target = model.resetTargets[key];
  if (!target) return draft;
  const placeholders = draft.placeholders.filter((k) => k !== key);
  return {
    rows: { ...draft.rows, [key]: { ...target } },
    placeholders: canBePlaceholder(model, key) ? [...placeholders, key] : placeholders,
  };
}

/** Comparable form: trimmed fields, no-note and empty-note alike, placeholders as a sorted set. */
function normalized(rows: BenchmarkDraft['rows'], placeholders: AdUnitBenchmarkKey[], order: AdUnitBenchmarkKey[]) {
  return JSON.stringify({
    rows: order.map((key) => {
      const row = rows[key];
      if (!row) return null;
      return [row.label, row.ctr, row.ctrRange, row.vcr, row.vcrRange, row.note ?? ''].map((v) => (v ?? '').trim());
    }),
    placeholders: order.filter((key) => placeholders.includes(key)),
  });
}

export const isDirty = (draft: BenchmarkDraft, model: BenchmarkTabModel): boolean =>
  normalized(draft.rows, draft.placeholders, model.order) !== normalized(model.rows, model.placeholders, model.order);

/** Trimmed row; a cleared note is sent as '' so the server keeps it cleared. */
const rowForSave = (row: AdUnitBenchmarkValues): AdUnitBenchmarkValues => ({
  label: row.label.trim(),
  ctr: row.ctr.trim(),
  ctrRange: row.ctrRange.trim(),
  vcr: row.vcr.trim(),
  vcrRange: row.vcrRange.trim(),
  note: (row.note ?? '').trim(),
});

/** Standard tab: the full record. */
export function standardSavePayload(
  draft: BenchmarkDraft,
  model: BenchmarkTabModel,
): Record<AdUnitFormatKey, AdUnitBenchmarkValues> {
  const out = {} as Record<AdUnitFormatKey, AdUnitBenchmarkValues>;
  for (const key of model.order) {
    const row = draft.rows[key];
    if (row && key !== 'sov-takeover') out[key] = rowForSave(row);
  }
  return out;
}

/** SOV tab: only the rows with their own numbers — the rest stay placeholders. */
export function sovSavePayload(
  draft: BenchmarkDraft,
  model: BenchmarkTabModel,
): Partial<Record<AdUnitBenchmarkKey, AdUnitBenchmarkValues>> {
  const out: Partial<Record<AdUnitBenchmarkKey, AdUnitBenchmarkValues>> = {};
  for (const key of model.order) {
    const row = draft.rows[key];
    if (row && !draft.placeholders.includes(key)) out[key] = rowForSave(row);
  }
  return out;
}
